import * as React from 'react';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import Shape from '../containers/Shape';
import { SketchMode, SlideData } from "../reducers";
import { InjectedIntlProps, injectIntl } from "react-intl";
import { SLIDE_CONTENT_HEIGHT, SLIDE_HEIGHT, SLIDE_WIDTH } from "../constants";

interface Props {
    readonly slide: SlideData,
    readonly dragging: boolean,
    readonly shapeDragging: boolean,
    readonly sketchMode: SketchMode,
    readonly zoomLevel: number,
    readonly calculateSVGPoint: (mouseX: number, mouseY: number) => {x: number, y: number},
    readonly stopPropagation: (event: React.MouseEvent<SVGElement>) => void,
    readonly onStartDragging: (event: React.MouseEvent<SVGElement>) => void,
    readonly onStartSketching: (event: React.MouseEvent<SVGElement>) => void,
    readonly onZoomInOrOut: (event: React.MouseEvent<SVGElement>) => void,
    readonly onDeleteButtonClicked: (event: React.MouseEvent<SVGElement>) => void,
    readonly onUpdateNotes: (event: React.MouseEvent<SVGElement>) => void,
}

const styles = (theme: Theme) => createStyles({
    root: {
        '&:hover $dragButton': {
            visibility: 'visible'
        },
        '&:hover $deleteButton': {
            visibility: 'visible'
        },
    },
    hotspot: {
        stroke: 'none',
        fill: 'transparent',
    },
    frame: {
        stroke: '#0a0a0a',
        fill: 'white',
        strokeWidth: 5,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    lines: {
        stroke: theme.palette.grey[500],
        fill: 'none',
        strokeWidth: 1,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeDasharray: '3, 4',
    },
    notesHeadline: {
        fill: theme.palette.grey[500],
        fontFamily: '"Gloria Hallelujah", cursive',
        fontWeight: 'bold',
        fontSize: 32,
        textAnchor: 'start',
        userSelect: 'none',
        '&:selection': {
            background: 'none'
        },
        whiteSpace: 'pre-wrap',
    },
    notes: {
        fill: theme.palette.grey[500],
        fontFamily: '"Gloria Hallelujah", cursive',
        fontWeight: 'bold',
        fontSize: 24,
        textAnchor: 'start',
        userSelect: 'none',
        '&:selection': {
            background: 'none'
        },
        whiteSpace: 'pre-wrap',
    },
    shapesContainer: {
        clipPath: `path('M0 0,H${SLIDE_WIDTH}V${SLIDE_CONTENT_HEIGHT}H-${SLIDE_WIDTH}Z')`,
    },
    draggedContent: {
        transform: 'scale(1.03, 1.03)'
    },
    button: {
        fill: theme.palette.grey[600],
        visibility: 'hidden',
        '&:hover': {
            fill: theme.palette.grey[700]
        },
    },
    dragButton: {},
    deleteButton: {
        cursor: 'pointer',
    },
});

type Styles =
    'root'
    | 'hotspot'
    | 'frame'
    | 'lines'
    | 'notesHeadline'
    | 'notes'
    | 'shapesContainer'
    | 'draggedContent'
    | 'button'
    | 'dragButton'
    | 'deleteButton';

const SlideView = ({slide, dragging, shapeDragging, sketchMode, zoomLevel, classes, intl, calculateSVGPoint, stopPropagation, onStartDragging, onStartSketching, onZoomInOrOut, onDeleteButtonClicked, onUpdateNotes}: Props & WithStyles<Styles> & InjectedIntlProps) => {
    const slideCursorClass = dragging || shapeDragging ? 'cursorGrabbing' : 'cursorDefault';
    const hotspotCursorClass = dragging || shapeDragging ? 'cursorGrabbing' : sketchMode !== 'NONE' ? 'cursorCrosshair' : 'cursorDefault';
    const dragButtonCursorClass = dragging || shapeDragging ? 'cursorGrabbing' : 'cursorGrab';
    const buttonsVisible = !dragging && !shapeDragging && sketchMode === 'NONE';
    return (
        <g
            className={`${classes.root} ${slideCursorClass}`}
            transform={`translate(${slide.x} ${slide.y})`}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
        >
            <g className={dragging ? classes.draggedContent : ''}>
                <path className={classes.frame} d={slide.pathData.frame}/>
                <path className={classes.lines} d={slide.pathData.lines}/>
                <text className={classes.notesHeadline} x={20} y={SLIDE_CONTENT_HEIGHT + 35}>{intl.formatMessage({id: 'notes_headline'})}</text>


                <clipPath id={`clip-slide-notes-${slide.uuid}`}>
                    <rect x={0}
                          y={0}
                          width={SLIDE_WIDTH}
                          height={SLIDE_HEIGHT - SLIDE_CONTENT_HEIGHT - 40}
                    />
                </clipPath>

                <g transform={`translate(20 ${SLIDE_CONTENT_HEIGHT + 40})`}
                   style={{clipPath: `url(#clip-slide-notes-${slide.uuid})`}}
                >
                    <text className={classes.notes}>
                        {slide.notesLines.map((line, index) => (
                            <tspan key={index} x={0} dy={30}>{line}</tspan>
                        ))}
                    </text>
                </g>

                <rect x={0}
                      y={0}
                      width={SLIDE_WIDTH}
                      height={SLIDE_CONTENT_HEIGHT}
                      className={`${classes.hotspot} ${hotspotCursorClass}`}
                      onMouseDown={onStartSketching}
                      onDoubleClick={onZoomInOrOut}
                />
                <rect x={0}
                      y={SLIDE_CONTENT_HEIGHT}
                      width={SLIDE_WIDTH}
                      height={SLIDE_HEIGHT - SLIDE_CONTENT_HEIGHT}
                      className={`${classes.hotspot} cursorPointer`}
                      onDoubleClick={onUpdateNotes}
                />

                <clipPath id={`clip-slide-${slide.uuid}`}>
                    <rect x={0}
                          y={0}
                          width={SLIDE_WIDTH}
                          height={SLIDE_CONTENT_HEIGHT}
                    />
                </clipPath>

                <g style={{clipPath: `url(#clip-slide-${slide.uuid})`}}>
                    {slide.shapes.map((shape, index) => (
                        <Shape key={index}
                               slideUuid={slide.uuid}
                               shape={shape}
                               calculateSVGPoint={calculateSVGPoint}
                        />
                    ))}
                </g>

                {buttonsVisible &&
                <g
                    className={`${classes.button} ${classes.deleteButton}`}
                    transform="translate(920 13)"
                    onClick={onDeleteButtonClicked}
                    onMouseDown={stopPropagation}
                    onDoubleClick={stopPropagation}
                >
                    <rect fill="transparent" x={-3} y={-3} width={30} height={30}/>
                    <path
                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    />
                    <path d="M0 0h24v24H0z" fill="none"/>
                </g>
                }

                {buttonsVisible &&
                <g
                    className={`${classes.button} ${classes.dragButton} ${dragButtonCursorClass}`}
                    transform={`translate(12 12) scale(${10 / zoomLevel})`}
                    onClick={stopPropagation}
                    onMouseDown={onStartDragging}
                    onDoubleClick={stopPropagation}
                >
                    <rect fill="transparent" width={24} height={24}/>
                    <path fill="none" d="M0 0h24v24H0V0z"/>
                    <path
                        d="M10.5 9h3c.28 0 .5-.22.5-.5V6h1.79c.45 0 .67-.54.35-.85l-3.79-3.79c-.2-.2-.51-.2-.71 0L7.85 5.15c-.31.31-.09.85.36.85H10v2.5c0 .28.22.5.5.5zm-2 1H6V8.21c0-.45-.54-.67-.85-.35l-3.79 3.79c-.2.2-.2.51 0 .71l3.79 3.79c.31.31.85.09.85-.36V14h2.5c.28 0 .5-.22.5-.5v-3c0-.28-.22-.5-.5-.5zm14.15 1.65l-3.79-3.79c-.32-.32-.86-.1-.86.35V10h-2.5c-.28 0-.5.22-.5.5v3c0 .28.22.5.5.5H18v1.79c0 .45.54.67.85.35l3.79-3.79c.2-.19.2-.51.01-.7zM13.5 15h-3c-.28 0-.5.22-.5.5V18H8.21c-.45 0-.67.54-.35.85l3.79 3.79c.2.2.51.2.71 0l3.79-3.79c.31-.31.09-.85-.35-.85H14v-2.5c0-.28-.22-.5-.5-.5z"/>
                </g>
                }
            </g>
        </g>
    );
};

export default withStyles(styles)(injectIntl(SlideView));



// WEBPACK FOOTER //
// ./src/components/SlideView.tsx